import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./baseQuery";
import { compactBody } from "../lib/adminFormat";

/**
 * Terakki Yönetimi (Admin) uçları: /api/panel/admin/*. Hepsi POST, düz JSON.
 * Sunucu `{ status, data }` döner; burada `data`yı açıp bileşenlere veriyoruz.
 * Boş isteğe bağlı alanlar gövdeden atılır (validator tip denetimine takılmasın).
 */
const unwrap = (response) => response?.data ?? response;

const post = (path, body = {}) => ({
  url: `/api/panel/admin${path}`,
  method: "POST",
  data: compactBody(body),
});

export const adminApi = createApi({
  reducerPath: "panelAdmin",
  baseQuery: axiosBaseQuery(),
  refetchOnReconnect: true,
  tagTypes: ["AdminDashboard", "AdminLibrary", "AdminPackage", "AdminUsers", "AdminAudit"],
  endpoints: (builder) => ({
    getDashboard: builder.query({
      query: () => post("/dashboard"),
      transformResponse: unwrap,
      providesTags: ["AdminDashboard"],
    }),

    // Kütüphane denetimi
    listLibrary: builder.query({
      query: ({ tab = "pending", q, page = 1 } = {}) => post("/library/list", { tab, q, page }),
      transformResponse: unwrap,
      providesTags: ["AdminLibrary"],
    }),
    getLibraryPackage: builder.query({
      query: ({ packageId }) => post("/library/get", { packageId }),
      transformResponse: unwrap,
      providesTags: (result, error, arg) => [{ type: "AdminPackage", id: arg?.packageId }],
    }),
    verifyPackage: builder.mutation({
      query: ({ packageId, verified }) => post("/library/verify", { packageId, verified }),
      transformResponse: unwrap,
      invalidatesTags: (result, error, arg) => [
        "AdminLibrary",
        "AdminDashboard",
        { type: "AdminPackage", id: arg?.packageId },
      ],
    }),
    recommendPackage: builder.mutation({
      query: ({ packageId, recommended }) => post("/library/recommend", { packageId, recommended }),
      transformResponse: unwrap,
      invalidatesTags: (result, error, arg) => [
        "AdminLibrary",
        "AdminDashboard",
        { type: "AdminPackage", id: arg?.packageId },
      ],
    }),
    deletePackage: builder.mutation({
      query: ({ packageId }) => post("/library/delete", { packageId }),
      transformResponse: unwrap,
      invalidatesTags: ["AdminLibrary", "AdminDashboard"],
    }),

    // Kullanıcılar
    listUsers: builder.query({
      query: ({ q, role, status, page = 1 } = {}) => post("/users/list", { q, role, status, page }),
      transformResponse: unwrap,
      providesTags: ["AdminUsers"],
    }),
    setUserActive: builder.mutation({
      query: ({ userId, active }) => post("/users/set-active", { userId, active }),
      transformResponse: unwrap,
      invalidatesTags: ["AdminUsers", "AdminDashboard"],
    }),
    unlockUser: builder.mutation({
      query: ({ email }) => post("/users/unlock", { email }),
      transformResponse: unwrap,
      invalidatesTags: ["AdminUsers"],
    }),
    sendUserReset: builder.mutation({
      query: ({ userId }) => post("/users/send-reset", { userId }),
      transformResponse: unwrap,
    }),

    // Duyuru hedefi için grup araması
    searchGroups: builder.query({
      query: ({ q } = {}) => post("/groups/search", { q }),
      transformResponse: unwrap,
    }),

    // Denetim kayıtları
    listAudit: builder.query({
      query: ({ category, type, q, from, to, page = 1 } = {}) =>
        post("/audit/list", { category, type, q, from, to, page }),
      transformResponse: unwrap,
      providesTags: ["AdminAudit"],
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useListLibraryQuery,
  useGetLibraryPackageQuery,
  useVerifyPackageMutation,
  useRecommendPackageMutation,
  useDeletePackageMutation,
  useListUsersQuery,
  useSetUserActiveMutation,
  useUnlockUserMutation,
  useSendUserResetMutation,
  useSearchGroupsQuery,
  useListAuditQuery,
} = adminApi;
